import React, { useEffect, useState } from "react";
import api from "../lib/apiClient";
import { STATUSES } from "../lib/statusConfig";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Combobox } from "./Combobox";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const empty = {
  grievant_name: "", discipline: "N/A", subject: "", rep: "",
  status: "Pending Submission", date_due: "", comments: "", case_no: "", eid: "",
};

export function GrievanceForm({ open, onOpenChange, existing, onSaved, division, bucket }) {
  const [form, setForm] = useState(empty);
  const [options, setOptions] = useState({ subjects: [], disciplines: [], reps: [], grievant_names: [] });
  const [grievantOptions, setGrievantOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(existing);
  const { isAdmin } = useAuth();

  const loadOptions = () => api.get("/options").then((r) => setOptions(r.data)).catch(() => {});
  const loadGrievantOptions = () =>
    api.get("/grievant-options", { params: { division: division || undefined } })
      .then((r) => setGrievantOptions(r.data || [])).catch(() => setGrievantOptions([]));

  useEffect(() => { if (open) { loadOptions(); loadGrievantOptions(); } }, [open, division]);

  useEffect(() => {
    if (open) {
      const src = existing ? { ...empty, ...existing } : empty;
      setForm({ ...src, date_due: src.date_due ? String(src.date_due).slice(0, 10) : "" });
    }
  }, [open, existing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canEditCaseNo = isEdit && isAdmin && ["fwd_office", "terminations"].includes(existing?.bucket);

  const addOption = (category) => async (value) => {
    try {
      const { data } = await api.post("/options", { category, value });
      setOptions((o) => ({ ...o, [category]: data.values }));
      toast.success(`Added “${value}”`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add option");
      throw e;
    }
  };

  const submit = async () => {
    if (!form.grievant_name.trim() || !form.subject.trim()) {
      toast.error("Grievant name and subject are required");
      return;
    }
    setSaving(true);
    const payload = {
      grievant_name: form.grievant_name, discipline: form.discipline, subject: form.subject,
      rep: form.rep, comments: form.comments, eid: form.eid || "",
      date_due: form.date_due ? String(form.date_due).slice(0, 10) : null,
    };
    if (canEditCaseNo && form.case_no.trim()) payload.case_no = form.case_no.trim();
    try {
      if (isEdit) {
        await api.put(`/grievances/${existing.id}`, payload);
        toast.success("Grievance updated");
      } else {
        await api.post("/grievances", {
          ...payload,
          status: form.status,
          division: division === "cargo" ? "ops" : (division || "ramp"),
          bucket: bucket || "local",
        });
        toast.success("Grievance filed");
      }
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error("Failed to save grievance");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg rounded-sm bg-card">
        <DialogHeader>
          <DialogTitle className="font-head tracking-tight">{isEdit ? "Edit Grievance" : "File New Grievance"}</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {isEdit ? "Update the details of this grievance case." : "Enter the details to file a new grievance case."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {canEditCaseNo && (
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider">Case Number</Label>
              <Input data-testid="form-case-no" className="rounded-sm font-mono" value={form.case_no}
                onChange={(e) => set("case_no", e.target.value)} placeholder="e.g. BNA-O-1152-2026" />
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider">Grievant Name</Label>
              <Combobox
                testid="form-grievant-name"
                value={form.grievant_name}
                valueEid={form.eid}
                onChange={(v, opt) => setForm((f) => ({ ...f, grievant_name: v, eid: opt && opt.eid !== undefined ? opt.eid : "" }))}
                options={grievantOptions.map((o) => ({ value: o.name, label: o.name, eid: o.eid }))}
                placeholder="Search grievant…"
                searchPlaceholder="Type a name or EID…"
                allowCreate
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider">Rep</Label>
              <Combobox
                testid="form-rep"
                value={form.rep}
                onChange={(v) => set("rep", v)}
                options={options.reps}
                placeholder="Select rep…"
                searchPlaceholder="Search reps…"
                allowCreate={isAdmin}
                onCreate={addOption("reps")}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider">Subject</Label>
            <Combobox
              testid="form-subject"
              value={form.subject}
              onChange={(v) => set("subject", v)}
              options={options.subjects}
              placeholder="Select subject…"
              searchPlaceholder="Search subjects…"
              allowCreate={isAdmin}
              onCreate={addOption("subjects")}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider">Discipline</Label>
              <Combobox
                testid="form-discipline"
                value={form.discipline}
                onChange={(v) => set("discipline", v)}
                options={options.disciplines}
                placeholder="Select discipline…"
                searchPlaceholder="Search disciplines…"
                allowCreate={isAdmin}
                onCreate={addOption("disciplines")}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider">Date Due</Label>
              <Input type="date" data-testid="form-date-due" className="rounded-sm" value={form.date_due}
                onChange={(e) => set("date_due", e.target.value)} />
            </div>
          </div>
          {!isEdit && (
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider">Initial Status</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="form-status" className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card">
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider">Comments</Label>
            <Textarea data-testid="form-comments" className="rounded-sm min-h-[70px]" value={form.comments}
              onChange={(e) => set("comments", e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="form-submit" className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90" onClick={submit} disabled={saving}>
            {saving ? "Saving…" : isEdit ? "Save Changes" : "File Grievance"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
